import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import ExploreIcon from '@mui/icons-material/Explore';
import HeightIcon from '@mui/icons-material/Height';
import TerrainIcon from '@mui/icons-material/Terrain';
import BarChartIcon from '@mui/icons-material/BarChart';
import CloseIcon from '@mui/icons-material/Close';
import SignalWifi4BarIcon from '@mui/icons-material/SignalWifi4Bar';
import MemoryIcon from '@mui/icons-material/Memory';
import SpeedIcon from '@mui/icons-material/Speed';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import SyncIcon from '@mui/icons-material/Sync';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot';
import CloudIcon from '@mui/icons-material/Cloud';
import ChangeHistoryIcon from '@mui/icons-material/ChangeHistory';
import { COLORS, TOOLTIP_STYLES, PANEL_STYLES } from '../styles/viewerStyles';
import { useMouseCoordinates } from '../hooks/useMouseCoordinates';
import { usePing } from '../hooks/usePing';
import { useTileStreamQuality } from '../hooks/useTileStreamQuality';

const ICON_SX = { fontSize: 13, color: '#fff', opacity: 0.6, mr: 0.25 };

const separator = (
  <Box sx={{
    width: '1px',
    height: 14,
    bgcolor: 'rgba(255,255,255,0.12)',
    mx: 1,
    flexShrink: 0,
  }} />
);

// ── Coordinate format types ─────────────────────────────────────────────────
const COORD_FORMATS = ['DD', 'DMS', 'UTM'];

function formatCoordDD(value, isLat) {
  const abs = Math.abs(value);
  const dir = isLat ? (value >= 0 ? 'N' : 'S') : (value >= 0 ? 'E' : 'W');
  return `${abs.toFixed(6)}\u00B0${dir}`;
}

function formatCoordDMS(value, isLat) {
  const abs = Math.abs(value);
  const deg = Math.floor(abs);
  const minFloat = (abs - deg) * 60;
  const min = Math.floor(minFloat);
  const sec = ((minFloat - min) * 60).toFixed(2);
  const dir = isLat ? (value >= 0 ? 'N' : 'S') : (value >= 0 ? 'E' : 'W');
  return `${deg}\u00B0${String(min).padStart(2, '0')}\u2032${String(sec).padStart(5, '0')}\u2033${dir}`;
}

function getUTMZone(lon) {
  return Math.floor((lon + 180) / 6) + 1;
}

function formatCoordUTM(lat, lon) {
  const zone = getUTMZone(lon);
  const band = lat >= 0 ? 'N' : 'S';

  const a = 6378137;
  const f = 1 / 298.257223563;
  const e2 = 2 * f - f * f;
  const e_prime2 = e2 / (1 - e2);
  const k0 = 0.9996;

  const lonRad = (lon * Math.PI) / 180;
  const latRad = (lat * Math.PI) / 180;
  const lon0 = ((zone - 1) * 6 - 180 + 3) * Math.PI / 180;

  const N = a / Math.sqrt(1 - e2 * Math.sin(latRad) ** 2);
  const T = Math.tan(latRad) ** 2;
  const C = e_prime2 * Math.cos(latRad) ** 2;
  const A = Math.cos(latRad) * (lonRad - lon0);

  const M = a * (
    (1 - e2 / 4 - 3 * e2 ** 2 / 64 - 5 * e2 ** 3 / 256) * latRad -
    (3 * e2 / 8 + 3 * e2 ** 2 / 32 + 45 * e2 ** 3 / 1024) * Math.sin(2 * latRad) +
    (15 * e2 ** 2 / 256 + 45 * e2 ** 3 / 1024) * Math.sin(4 * latRad) -
    (35 * e2 ** 3 / 3072) * Math.sin(6 * latRad)
  );

  const easting = k0 * N * (A + (1 - T + C) * A ** 3 / 6 +
    (5 - 18 * T + T ** 2 + 72 * C - 58 * e_prime2) * A ** 5 / 120) + 500000;

  let northing = k0 * (M + N * Math.tan(latRad) * (
    A ** 2 / 2 + (5 - T + 9 * C + 4 * C ** 2) * A ** 4 / 24 +
    (61 - 58 * T + T ** 2 + 600 * C - 330 * e_prime2) * A ** 6 / 720
  ));
  if (lat < 0) northing += 10000000;

  return `${zone}${band} ${easting.toFixed(0)}E ${northing.toFixed(0)}N`;
}

function formatAngle(value) {
  return `${Math.round(value)}\u00B0`;
}

function formatNumber(n) {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return `${n}`;
}

const monoStyle = {
  color: '#fff',
  fontSize: '0.7rem',
  fontFamily: '"JetBrains Mono", "SF Mono", "Fira Code", monospace',
  lineHeight: 1,
  display: 'flex',
  alignItems: 'center',
};

const animatedMonoStyle = {
  ...monoStyle,
  transition: 'opacity 0.15s ease-out',
  minWidth: 0,
  whiteSpace: 'nowrap',
};

function useFps(interval = 1000) {
  const [fps, setFps] = useState(0);
  const frameCount = useRef(0);
  const lastTime = useRef(performance.now());

  useEffect(() => {
    let rafId;
    const tick = () => {
      frameCount.current++;
      const now = performance.now();
      if (now - lastTime.current >= interval) {
        setFps(Math.round((frameCount.current * 1000) / (now - lastTime.current)));
        frameCount.current = 0;
        lastTime.current = now;
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [interval]);

  return fps;
}

function Sparkline({ data, color, width = 40, height = 14 }) {
  const max = Math.max(...data.map(v => v ?? 0), 0.1);
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v ?? 0) / max) * (height - 1);
    return [x, y];
  });
  const line = pts.map(([x, y]) => `${x},${y}`).join(' ');
  const area = `0,${height} ` + line + ` ${width},${height}`;
  return (
    <svg width={width} height={height} style={{ display: 'block', flexShrink: 0 }}>
      <polygon points={area} fill={color} fillOpacity={0.15} />
      <polyline points={line} fill="none" stroke={color} strokeWidth={1.5} strokeOpacity={0.8} />
    </svg>
  );
}

function SmoothNumber({ value, decimals = 1, suffix = '', style }) {
  const displayRef = useRef(null);
  const currentRef = useRef(value ?? 0);
  const targetRef = useRef(value ?? 0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (value === null || value === undefined) return;
    targetRef.current = value;

    const animate = () => {
      const diff = targetRef.current - currentRef.current;
      if (Math.abs(diff) < 0.01) {
        currentRef.current = targetRef.current;
      } else {
        currentRef.current += diff * 0.25;
      }
      if (displayRef.current) {
        displayRef.current.textContent = `${currentRef.current.toFixed(decimals)}${suffix}`;
      }
      if (Math.abs(targetRef.current - currentRef.current) > 0.001) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [value, decimals, suffix]);

  const initial = value !== null && value !== undefined ? `${value.toFixed(decimals)}${suffix}` : '---';
  return <span ref={displayRef} style={style}>{initial}</span>;
}

// ── Statistics Panel ────────────────────────────────────────────────────────

const STAT_ROW = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  py: 0.4,
  px: 0.5,
};

const STAT_LABEL = {
  ...monoStyle,
  fontSize: '0.65rem',
  opacity: 0.6,
  flex: '1 1 auto',
  ml: 0.75,
};

const STAT_VALUE = {
  ...monoStyle,
  fontSize: '0.7rem',
  textAlign: 'right',
  flex: '0 0 auto',
};

const STAT_ICON = { fontSize: 12, color: 'rgba(255,255,255,0.5)' };

const SECTION_TITLE = {
  ...monoStyle,
  fontSize: '0.7rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  opacity: 0.65,
  pt: 1.5,
  pb: 0.5,
  px: 0.5,
  color: 'rgba(255, 255, 255, 0.99)',
  '&:first-of-type': { pt: 0.25 },
};

function StatRow({ icon, label, value, color }) {
  return (
    <Box sx={STAT_ROW}>
      <Box sx={{ display: 'flex', alignItems: 'center', flex: '1 1 auto', minWidth: 0 }}>
        {icon}
        <Typography sx={STAT_LABEL}>{label}</Typography>
      </Box>
      <Typography sx={{ ...STAT_VALUE, color: color || '#fff' }}>{value}</Typography>
    </Box>
  );
}

function StatsPanel({ ping, stream, fps, t, onClose, hasGaussianOnly }) {
  const fpsColor = fps >= 30 ? '#4caf50' : fps >= 15 ? '#ff9800' : '#f44336';
  const { stats, history, color: streamColor } = stream;

  return (
    <Box
      sx={{
        ...PANEL_STYLES.containerNoPadding(240),
        position: 'absolute',
        top: 'auto',
        bottom: 34,
        left: 8,
        py: 1.5,
        px: 1,
      }}
    >
      {/* Close button */}
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          p: 0.25,
          color: 'rgba(255,255,255,0.5)',
          '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
        }}
      >
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>

      {/* Performance */}
      <Typography sx={SECTION_TITLE}>{t('StatsPerformance') || 'Performance'}</Typography>
      <StatRow
        icon={<SpeedIcon sx={STAT_ICON} />}
        label="FPS"
        value={fps}
        color={fpsColor}
      />
      <StatRow
        icon={<SignalWifi4BarIcon sx={STAT_ICON} />}
        label={t('StatsPing') || 'Ping'}
        value={ping.label}
        color={ping.color}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', px: 0.5, pb: 0.25 }}>
        <Box sx={{ flex: 1 }}>
          <Sparkline data={ping.history} color={ping.color} width={220} height={16} />
        </Box>
      </Box>

      {/* Tiles */}
      {!hasGaussianOnly && (
        <>
          <Typography sx={SECTION_TITLE}>{t('StatsTiles') || 'Tiles'}</Typography>
          <StatRow
            icon={<HourglassBottomIcon sx={STAT_ICON} />}
            label={t('StatsPending') || 'Pending'}
            value={stats.pendingRequests}
            color={streamColor}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', px: 0.5, pb: 0.25 }}>
            <Box sx={{ flex: 1 }}>
              <Sparkline data={history} color={streamColor} width={220} height={16} />
            </Box>
          </Box>
          <StatRow
            icon={<SyncIcon sx={STAT_ICON} />}
            label={t('StatsProcessing') || 'Processing'}
            value={stats.tilesProcessing}
          />
          <StatRow
            icon={<CloudDownloadIcon sx={STAT_ICON} />}
            label={t('StatsLoaded') || 'Loaded'}
            value={`${stats.tilesLoaded} / ${stats.tilesTotal}`}
          />
          <StatRow
            icon={<VisibilityIcon sx={STAT_ICON} />}
            label={t('StatsRendered') || 'Rendered'}
            value={stats.tilesSelected}
          />
          {stats.failCount > 0 && (
            <StatRow
              icon={<ErrorOutlineIcon sx={STAT_ICON} />}
              label={t('StatsFailed') || 'Failed'}
              value={stats.failCount}
              color="#f44336"
            />
          )}
        </>
      )}

      {/* Points & Triangles */}
      {hasGaussianOnly ? (
        stats.pointsLoaded > 0 && (
          <>
            <Typography sx={SECTION_TITLE}>{t('StatsData') || 'Data'}</Typography>
            <StatRow
              icon={<ScatterPlotIcon sx={STAT_ICON} />}
              label={t('StatsGaussianSplats') || 'Gaussian splats'}
              value={formatNumber(stats.pointsLoaded)}
            />
          </>
        )
      ) : (
        (stats.pointsSelected > 0 || stats.trianglesSelected > 0) && (
          <>
            <Typography sx={SECTION_TITLE}>{t('StatsData') || 'Data'}</Typography>
            {stats.pointsSelected > 0 && (
              <StatRow
                icon={<ScatterPlotIcon sx={STAT_ICON} />}
                label={t('StatsPointsInTiles') || 'Points in rendered tiles'}
                value={formatNumber(stats.pointsSelected)}
              />
            )}
            {stats.pointsLoaded > 0 && (
              <StatRow
                icon={<CloudIcon sx={STAT_ICON} />}
                label={t('StatsPointsInMemory') || 'Points in memory'}
                value={formatNumber(stats.pointsLoaded)}
              />
            )}
            {stats.trianglesSelected > 0 && (
              <StatRow
                icon={<ChangeHistoryIcon sx={STAT_ICON} />}
                label={t('StatsTriangles') || 'Triangles'}
                value={formatNumber(stats.trianglesSelected)}
              />
            )}
          </>
        )
      )}

      {/* GPU Memory */}
      {stats.totalBytes > 0 && (
        <>
          <Typography sx={SECTION_TITLE}>{t('StatsMemory') || 'GPU Memory'}</Typography>
          <StatRow
            icon={<MemoryIcon sx={STAT_ICON} />}
            label={t('StatsTotal') || 'Total'}
            value={stats.totalLabel}
          />
        </>
      )}
    </Box>
  );
}

// ── Main StatusBar ──────────────────────────────────────────────────────────

export function StatusBar({ viewer, hasGaussianOnly }) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 600px)');
  const { latitude, longitude, altitude, cameraHeight, heading, pitch } = useMouseCoordinates(viewer);
  const ping = usePing();
  const stream = useTileStreamQuality(viewer);
  const fps = useFps();
  const [statsOpen, setStatsOpen] = useState(false);

  const [coordFormat, setCoordFormat] = useState(() => {
    try { return localStorage.getItem('viewer_coord_format') || 'DD'; } catch { return 'DD'; }
  });

  const cycleFormat = useCallback(() => {
    setCoordFormat(prev => {
      const idx = COORD_FORMATS.indexOf(prev);
      const next = COORD_FORMATS[(idx + 1) % COORD_FORMATS.length];
      try { localStorage.setItem('viewer_coord_format', next); } catch { /* noop */ }
      return next;
    });
  }, []);

  const hasCoordinates = latitude !== null && longitude !== null;

  const renderCoordinates = () => {
    if (!hasCoordinates) {
      return <Typography sx={{ ...animatedMonoStyle, opacity: 0.3 }}>---</Typography>;
    }

    if (coordFormat === 'UTM') {
      return (
        <Typography sx={animatedMonoStyle}>
          {formatCoordUTM(latitude, longitude)}
        </Typography>
      );
    }

    const formatter = coordFormat === 'DMS' ? formatCoordDMS : formatCoordDD;
    return (
      <>
        <Typography sx={animatedMonoStyle}>
          {formatter(latitude, true)}
        </Typography>
        <Box sx={{ width: 12 }} />
        <Typography sx={animatedMonoStyle}>
          {formatter(longitude, false)}
        </Typography>
      </>
    );
  };

  const formatLabel = coordFormat === 'DD' ? 'DD' : coordFormat === 'DMS' ? 'DMS' : 'UTM';

  // Overall health dot color — worst of ping, stream, fps
  const fpsColor = fps >= 30 ? '#4caf50' : fps >= 15 ? '#ff9800' : '#f44336';
  const healthColor = [ping.color, stream.color, fpsColor].includes('#f44336')
    ? '#f44336'
    : [ping.color, stream.color, fpsColor].includes('#ff9800')
      ? '#ff9800'
      : '#4caf50';

  return (
    <>
    {/* Statistics panel — rendered outside status bar so backdropFilter works */}
    {statsOpen && <StatsPanel ping={ping} stream={stream} fps={fps} t={t} onClose={() => setStatsOpen(false)} hasGaussianOnly={hasGaussianOnly} />}

    <Box
      sx={{
        position: 'absolute',
        bottom: 'env(safe-area-inset-bottom, 0px)',
        left: 0,
        right: 0,
        height: 28,
        bgcolor: COLORS.darkBg,
        backdropFilter: 'blur(16px)',
        borderTop: `1px solid ${COLORS.panelBorder}`,
        boxShadow: '0 -4px 32px rgba(0,0,0,0.3)',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        px: 2,
        zIndex: 1100,
      }}
    >
      {/* Left: statistics icon */}
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: '0 1 auto' }}>
        <Tooltip
          title={t('StatsTooltip') || 'Statistics'}
          placement="top"
          arrow
          slotProps={{ tooltip: { sx: TOOLTIP_STYLES.tooltip }, arrow: { sx: TOOLTIP_STYLES.arrow } }}
        >
          <IconButton
            onClick={() => setStatsOpen(prev => !prev)}
            sx={{
              p: 0.25,
              color: healthColor,
              transition: 'color 0.3s ease',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
            }}
          >
            <BarChartIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>

        {/* Compact inline summary */}
        <Typography sx={{ ...animatedMonoStyle, color: fpsColor, ml: 0.5 }}>
          {fps} FPS
        </Typography>
      </Box>

      {/* Center: cursor coordinates + altitude (desktop only) */}
      <Box sx={{ display: isMobile ? 'none' : 'flex', alignItems: 'center' }}>
        <Tooltip
          title={t('CoordFormatTooltip') || 'Click to change format (DD / DMS / UTM)'}
          placement="top"
          arrow
          slotProps={{ tooltip: { sx: TOOLTIP_STYLES.tooltip }, arrow: { sx: TOOLTIP_STYLES.arrow } }}
        >
          <Box
            onClick={cycleFormat}
            sx={{
              cursor: 'pointer',
              px: 0.75,
              py: 0.25,
              mr: 1,
              borderRadius: '4px',
              bgcolor: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.12)',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.15)',
                border: '1px solid rgba(255,255,255,0.25)',
              },
              '&:active': {
                transform: 'scale(0.95)',
              },
              userSelect: 'none',
            }}
          >
            <Typography sx={{
              ...monoStyle,
              fontSize: '0.6rem',
              fontWeight: 600,
              letterSpacing: '0.05em',
              opacity: 0.7,
            }}>
              {formatLabel}
            </Typography>
          </Box>
        </Tooltip>

        {renderCoordinates()}

        {altitude !== null && hasCoordinates && (
          <>
            <Box sx={{ width: 12 }} />
            <TerrainIcon sx={ICON_SX} />
            <Typography sx={animatedMonoStyle}>
              <SmoothNumber value={altitude} decimals={1} suffix=" m" />
            </Typography>
          </>
        )}
      </Box>

      {/* Right: camera heading/pitch + height */}
      <Box sx={{ display: 'flex', alignItems: 'center', flex: '0 1 auto', justifyContent: 'flex-end' }}>
        <Typography sx={{ ...animatedMonoStyle, opacity: 0.5, mr: 1, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {t('Camera') || 'camera'}
        </Typography>
        {heading !== null && pitch !== null && (
          <>
            <ExploreIcon sx={ICON_SX} />
            <Typography sx={animatedMonoStyle}>
              {formatAngle(heading)}
            </Typography>
            <Box sx={{ width: 6 }} />
            <Typography sx={animatedMonoStyle}>
              {formatAngle(pitch)}
            </Typography>
            {separator}
          </>
        )}
        <HeightIcon sx={ICON_SX} />
        <Typography sx={animatedMonoStyle}>
          {cameraHeight !== null ? <SmoothNumber value={cameraHeight} decimals={1} suffix=" m" /> : '---'}
        </Typography>
      </Box>
    </Box>
    </>
  );
}

StatusBar.propTypes = {
  viewer: PropTypes.object,
  hasGaussianOnly: PropTypes.bool,
};
